import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedUserRole } from '@/hooks/useOptimizedUserRole';

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-gradient flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    // Preserve the current location as returnUrl for post-login redirect
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?returnUrl=${returnUrl}`} replace />;
  }
  
  return <>{children}</>;
};

export const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-gradient flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  // Allow access to auth page if user is in reset password mode
  const isResetMode = location.search.includes('reset=true') || 
                      (location.search.includes('type=recovery') && location.search.includes('token'));
  
  if (user && !isResetMode) {
    return <Navigate to="/assessments" replace />;
  }
  
  return <>{children}</>;
};

/**
 * Route guard that requires both authentication AND terms acceptance.
 * Users who haven't accepted terms will be redirected to /accept-terms.
 */
export const TermsRequiredRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkTermsAcceptance = async () => {
      if (!user) {
        setIsChecking(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('terms_accepted')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking terms acceptance:', error);
          setTermsAccepted(false);
          setIsChecking(false);
          return;
        }

        setTermsAccepted(data?.terms_accepted ?? false);
      } catch (error) {
        console.error('Error checking terms acceptance:', error);
        setTermsAccepted(false);
      } finally {
        setIsChecking(false);
      }
    };

    if (user) {
      checkTermsAcceptance();
    } else {
      setIsChecking(false);
    }
  }, [user]);

  // Show loading while checking auth or terms status
  if (authLoading || isChecking) {
    return (
      <div className="min-h-screen bg-brand-gradient flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?returnUrl=${returnUrl}`} replace />;
  }

  // Authenticated but terms not accepted - redirect to accept-terms page
  if (termsAccepted === false) {
    // Store the intended destination for after terms acceptance
    sessionStorage.setItem('returnUrl', location.pathname + location.search);
    return <Navigate to="/accept-terms" replace />;
  }

  return <>{children}</>;
};

/**
 * Route guard requiring authentication, terms acceptance, AND admin role.
 * Server-side RLS still enforces all data access; this prevents admin UI
 * scaffolding from rendering for non-admin users (defense in depth).
 */
export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { isAdmin, isLoading: roleLoading } = useOptimizedUserRole();
  const location = useLocation();
  const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkTerms = async () => {
      if (!user) {
        setIsChecking(false);
        return;
      }
      try {
        const { data } = await supabase
          .from('profiles')
          .select('terms_accepted')
          .eq('id', user.id)
          .single();
        setTermsAccepted(data?.terms_accepted ?? false);
      } catch {
        setTermsAccepted(false);
      } finally {
        setIsChecking(false);
      }
    };
    if (user) checkTerms();
    else setIsChecking(false);
  }, [user]);

  if (authLoading || roleLoading || isChecking) {
    return (
      <div className="min-h-screen bg-brand-gradient flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?returnUrl=${returnUrl}`} replace />;
  }

  if (termsAccepted === false) {
    sessionStorage.setItem('returnUrl', location.pathname + location.search);
    return <Navigate to="/accept-terms" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
